import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { PublicEdufadSummary, PublicStats } from "@edufadder/shared-types";
import { colors, fonts, radii, spacing } from "@edufadder/ui";
import { AUTHORS } from "../content/authors.js";
import { EvidenceChip, topicLabel } from "../components/EvidenceChip.js";
import { LogoMark } from "../components/Logo.js";
import { PassageChecker } from "../components/PassageChecker.js";
import * as api from "../lib/api.js";
import { useExtensionStatus } from "../lib/extension-status.js";
import * as s from "../styles.js";

export function HomePage(): JSX.Element {
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [edufads, setEdufads] = useState<PublicEdufadSummary[]>([]);
  const ext = useExtensionStatus();

  useEffect(() => {
    api.getPublicStats().then(setStats).catch(() => setStats(null));
    api.listPublicEdufads().then(setEdufads).catch(() => setEdufads([]));
  }, []);

  const featured = stats && stats.trending.length > 0 ? null : edufads.slice(0, 6);

  return (
    <div>
      {/* Hero */}
      <section style={{ background: colors.brand, color: "#fff", borderBottom: `6px solid ${colors.gold}` }}>
        <div style={{ ...s.wide, paddingTop: spacing(12), paddingBottom: spacing(12) }}>
          <div className="ef-grid-2" style={{ alignItems: "center", gap: spacing(10) }}>
            <div>
              <p style={{ color: colors.gold, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", fontSize: 13, margin: 0 }}>
                Free · No account needed · Private by design
              </p>
              <h1 style={{ fontFamily: fonts.serif, fontSize: 42, lineHeight: 1.15, margin: `${spacing(3)} 0` }}>
                Notice the education ideas you meet online — and see what research says.
              </h1>
              <p style={{ fontSize: 18, lineHeight: 1.6, color: "#f3eee2", margin: `0 0 ${spacing(6)}` }}>
                EduFadder is a Chrome extension that recognises popular education ideas — <em>“edufads”</em> such as learning
                styles — as you browse, search, and read, and offers a short, sourced research note right where the idea
                appears. Awareness, not verdicts.
              </p>
              <div style={{ display: "flex", gap: spacing(3), flexWrap: "wrap" }}>
                <Link to="/get-extension" style={{ ...s.buttonGold, fontSize: 16, padding: `${spacing(3)} ${spacing(6)}` }}>
                  {ext.installed ? "Extension installed ✓ — manage" : "Get the free extension"}
                </Link>
                <Link
                  to="/check"
                  style={{ ...s.buttonOnDark, fontSize: 16, padding: `${spacing(3)} ${spacing(6)}`, textDecoration: "none", display: "inline-block" }}
                >
                  Check a passage now
                </Link>
              </div>
            </div>
            <div>
              <img
                src="/media/edufadder-demo.gif"
                alt="Animation: a web page about study tips; EduFadder highlights “learning style” and opens a research note beside it."
                className="ef-animated"
                style={{ width: "100%", borderRadius: radii.lg, border: `3px solid ${colors.gold}`, background: "#fff", display: "block" }}
              />
              <img
                src="/media/edufadder-note.png"
                alt="A research note from EduFadder anchored beside a highlighted phrase on a web page."
                className="ef-static"
                style={{ width: "100%", borderRadius: radii.lg, border: `3px solid ${colors.gold}`, background: "#fff" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Three ways it helps */}
      <section style={s.wide} aria-labelledby="how">
        <h2 id="how" style={{ ...s.sectionTitle, marginTop: 0 }}>How EduFadder helps — right where you are</h2>
        <hr style={s.goldRule} />
        <div className="ef-grid-3">
          <HelpCard
            title="While you browse & read"
            text="Install it and keep reading — nothing to click. As soon as a page mentions a known edufad, EduFadder highlights the phrase and opens a short summary of what research says. Select it for the full note and sources."
          />
          <HelpCard
            title="While you search"
            text="On Google, Bing, DuckDuckGo and others, EduFadder adds research notes when your search or the results mention an edufad. This happens entirely on your device — your searches are never sent anywhere."
          />
          <HelpCard
            title="Without the extension"
            text="Paste a passage below to see whether it mentions an idea researchers have studied, or browse the edufads library. It stays quiet unless it's confident — most text gets no note at all, on purpose."
          />
        </div>
      </section>

      {/* Live checker */}
      <section style={{ background: colors.goldTint, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
        <div style={s.wide}>
          <h2 style={{ ...s.sectionTitle, marginTop: 0 }}>Try it right now — no install</h2>
          <hr style={s.goldRule} />
          <PassageChecker compact />
        </div>
      </section>

      {/* Trending / featured */}
      <section style={s.wide}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: spacing(3) }}>
          <h2 style={{ ...s.sectionTitle, marginTop: 0 }}>
            {featured ? "Ideas EduFadder knows about" : "Most-encountered edufads this month"}
          </h2>
          <Link to="/edufads" style={{ fontWeight: 700 }}>
            See all {stats?.edufad_count ?? ""} edufads →
          </Link>
        </div>
        <hr style={s.goldRule} />
        <div className="ef-grid-3">
          {(featured ?? []).map((e) => (
            <EdufadCard key={e.id} id={e.id} label={e.label} topic={e.topic} strength={e.evidence_strength} />
          ))}
          {!featured &&
            stats?.trending.map((t) => (
              <EdufadCard key={t.id} id={t.id} label={t.label} strength={t.evidence_strength} extra={`Flagged ${t.times_flagged} times in the last 30 days`} />
            ))}
        </div>
        {stats && (
          <p style={{ ...s.mutedText, marginTop: spacing(4) }}>
            Evidence base {stats.evidence_base_version}
            {stats.evidence_base_published_at && `, published ${new Date(stats.evidence_base_published_at).toLocaleDateString()}`}. Every
            summary is curated and reviewed, and cites its sources.
          </p>
        )}
      </section>

      {/* Principles */}
      <section style={{ ...s.wide, paddingTop: 0 }}>
        <h2 style={s.sectionTitle}>Built on three promises</h2>
        <hr style={s.goldRule} />
        <div className="ef-grid-3">
          <HelpCard title="Awareness, not judgement" text="Notes describe where research stands — “not well supported so far”, “research is mixed”, “well supported” — and what's still worth knowing. They never call a page wrong." />
          <HelpCard title="Quiet unless confident" text="EduFadder abstains by default. It only speaks up when a passage clearly matches an idea it has curated, reviewed evidence for." />
          <HelpCard title="Private by design" text="Pages are checked on your device first; only passages that mention a known idea are looked at more closely. No browsing history, no URLs, no searches, and nothing used to train models." />
        </div>
      </section>

      {/* Creators */}
      <section style={{ ...s.wide, paddingTop: 0 }}>
        <div style={{ ...s.card, display: "flex", gap: spacing(6), alignItems: "center", flexWrap: "wrap", borderLeft: `6px solid ${colors.brand}` }}>
          <LogoMark size={56} />
          <div style={{ flex: 1, minWidth: 260 }}>
            <p style={{ margin: 0, fontFamily: fonts.serif, fontSize: 18, fontWeight: 700 }}>
              Created by {AUTHORS.map((a) => `${a.honorific ? `${a.honorific} ` : ""}${a.name}`).join(" and ")}
            </p>
            <p style={{ margin: `${spacing(1)} 0 0`, color: colors.muted }}>
              Instructional Systems &amp; Learning Technologies, Florida State University.{" "}
              <Link to="/about">Why we built EduFadder →</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function HelpCard({ title, text }: { title: string; text: string }): JSX.Element {
  return (
    <div style={{ ...s.card, marginBottom: 0, borderTop: `4px solid ${colors.gold}` }}>
      <h3 style={{ fontFamily: fonts.serif, fontSize: 19, margin: `0 0 ${spacing(2)}`, color: colors.brand }}>{title}</h3>
      <p style={{ margin: 0, lineHeight: 1.6 }}>{text}</p>
    </div>
  );
}

export function EdufadCard({ id, label, topic, strength, extra }: { id: string; label: string; topic?: string; strength: string; extra?: string }): JSX.Element {
  return (
    <Link to={`/edufads/${id}`} style={{ textDecoration: "none", color: "inherit" }}>
      <div style={{ ...s.card, marginBottom: 0, height: "100%", display: "flex", flexDirection: "column", gap: spacing(2) }}>
        {topic && <span style={{ ...s.mutedText, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, fontSize: 11 }}>{topicLabel(topic)}</span>}
        <span style={{ fontFamily: fonts.serif, fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>{label}</span>
        <span>
          <EvidenceChip strength={strength} />
        </span>
        {extra && <span style={s.mutedText}>{extra}</span>}
      </div>
    </Link>
  );
}
